/**
 * ProductReviewsList Component
 *
 * Yorumları gösteren komponent (sayfalama ile)
 * Server-side render için uygun
 * Circular overall rating ve kategori breakdowns
 */

"use client";

import { useState, useCallback, useEffect, useMemo } from 'react';
import { Review } from '@/hooks/useProductReviews';
import { transformMediaUrl } from '@/lib/media-url-transformer';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import {
    PRODUCT_REVIEW_RATING_CATEGORIES,
    collectRatingsForAverage,
    resolveCategoryRating,
    reviewCategoryLabel,
} from '@/lib/product-review-rating-categories';

export interface ProductReviewsListProps {
    reviews: Review[];
    averageRating: number;
    reviewCount: number;
    isLoading?: boolean;
    language?: string;
    productId?: number | null;
    languageId?: number | null;
    onLoadMore?: () => Promise<void>;
    hasMore?: boolean;
    translations?: Record<string, string>;
}

export default function ProductReviewsList({
    reviews,
    averageRating,
    reviewCount,
    isLoading = false,
    language = 'en',
    productId,
    languageId,
    onLoadMore,
    hasMore = false,
    translations = {},
}: ProductReviewsListProps) {
    const { getVariable } = useLanguageVariables();
    const t = useCallback(
        (key: string, fallback = '') => translations[key] || getVariable(key, fallback),
        [translations, getVariable],
    );

    const [loadingMore, setLoadingMore] = useState(false);
    const [allReviews, setAllReviews] = useState<Review[]>(reviews);
    const [hasMoreReviews, setHasMoreReviews] = useState(hasMore);

    useEffect(() => {
        setAllReviews(reviews);
        setHasMoreReviews(hasMore);
    }, [reviews, hasMore]);

    const categoryAverages = useMemo(() => {
        const categorySums: Record<string, { sum: number; count: number }> = {};

        PRODUCT_REVIEW_RATING_CATEGORIES.forEach((cat) => {
            categorySums[cat.key] = { sum: 0, count: 0 };
        });

        allReviews.forEach((review) => {
            PRODUCT_REVIEW_RATING_CATEGORIES.forEach((cat) => {
                const rating = resolveCategoryRating(review.rating_categories, cat.key, cat.legacyKeys);
                if (rating !== undefined) {
                    categorySums[cat.key].sum += rating;
                    categorySums[cat.key].count += 1;
                }
            });
        });

        const averages: Record<string, number> = {};
        PRODUCT_REVIEW_RATING_CATEGORIES.forEach((cat) => {
            const { sum, count } = categorySums[cat.key];
            averages[cat.key] = count > 0 ? sum / count : 0;
        });

        return averages;
    }, [allReviews]);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !hasMoreReviews || !onLoadMore) {
            return;
        }

        setLoadingMore(true);
        try {
            await onLoadMore();
        } catch (error) {
            console.error('Load more reviews error:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMoreReviews, onLoadMore]);

    const calculatedAverageRating = useMemo(() => {
        if (allReviews.length === 0) return 0;

        const totalRating = allReviews.reduce((sum, review) => {
            const categoryRatings = collectRatingsForAverage(review.rating_categories);
            if (categoryRatings.length > 0) {
                return sum + categoryRatings.reduce((a, b) => a + b, 0) / categoryRatings.length;
            }

            if (review.rating && review.rating > 0) {
                return sum + review.rating;
            }

            return sum;
        }, 0);

        return allReviews.length > 0 ? totalRating / allReviews.length : 0;
    }, [allReviews]);

    const finalAverageRating = averageRating > 0 ? averageRating : calculatedAverageRating;
    const finalReviewCount = reviewCount > 0 ? reviewCount : allReviews.length;
    const showEmptyState = finalReviewCount === 0 && allReviews.length === 0;

    const reviewCountLabel =
        finalReviewCount === 1
            ? t('product_reviews.review_singular', 'review')
            : t('product_reviews.review_plural', 'reviews');

    const ratingPercentage = finalAverageRating > 0 ? (finalAverageRating / 5) * 100 : 0;
    const circumference = 2 * Math.PI * 45;
    const offset = circumference - (ratingPercentage / 100) * circumference;

    if (isLoading && allReviews.length === 0) {
        return (
            <section className="mb-16 scroll-mt-24">
                <div className="h-10 w-48 bg-gray-200 rounded-lg animate-pulse mb-8" aria-hidden />
                <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gray-200 animate-pulse mb-4" />
                            <div className="h-4 w-28 bg-gray-200 rounded animate-pulse mb-2" />
                            <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                        </div>
                        <div className="space-y-4">
                            {PRODUCT_REVIEW_RATING_CATEGORIES.map((category) => (
                                <div key={category.key} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                                        <div className="h-4 w-12 bg-gray-100 rounded animate-pulse" />
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-gray-200 rounded-full animate-pulse" style={{ width: '75%' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                                    <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                                    <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
                                    <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section className="mb-16 scroll-mt-24">
            <h2 className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-8">
                {t('product_reviews.title', 'Customer Review')}
            </h2>

            {showEmptyState ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center mb-8">
                    <p className="text-alsero-light-gray text-lg">
                        {t('product_reviews.no_reviews', 'No reviews yet.')}
                    </p>
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="flex flex-col items-center justify-center">
                                <div className="relative w-32 h-32 mb-4">
                                    <svg className="transform -rotate-90 w-32 h-32">
                                        <circle cx="64" cy="64" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                                        <circle
                                            cx="64"
                                            cy="64"
                                            r="45"
                                            fill="none"
                                            stroke="#dc2626"
                                            strokeWidth="8"
                                            strokeDasharray={circumference}
                                            strokeDashoffset={offset}
                                            strokeLinecap="round"
                                            className="transition-all duration-500"
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <div className="text-3xl font-bold text-alsero-dark-gray">
                                            {finalAverageRating.toFixed(1)}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <p className="text-lg font-semibold text-alsero-dark-gray mb-1">
                                        {t('product_reviews.overall_ratings', 'Overall Ratings')}
                                    </p>
                                    <p className="text-sm text-alsero-light-gray">
                                        {t('product_reviews.based_on', 'Based on')} {finalReviewCount} {reviewCountLabel}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {PRODUCT_REVIEW_RATING_CATEGORIES.map((category) => {
                                    const categoryAvg = categoryAverages[category.key] || 0;
                                    const categoryPercentage = (categoryAvg / 5) * 100;
                                    const roundedRating = Math.round(categoryAvg * 10) / 10;
                                    const categoryLabel = reviewCategoryLabel(category, language, t);
                                    const ratingSubLabel = t('product_reviews.rating_value', 'Rating {{value}}').replace(
                                        '{{value}}',
                                        roundedRating.toFixed(1),
                                    );

                                    return (
                                        <div key={category.key} className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-semibold text-alsero-dark-gray">{categoryLabel}</p>
                                                    <p className="text-sm text-alsero-light-gray">{ratingSubLabel}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex text-yellow-400">
                                                        {[...Array(5)].map((_, i) => (
                                                            <svg
                                                                key={i}
                                                                className={`w-4 h-4 ${
                                                                    i < Math.round(categoryAvg) ? 'fill-current' : 'fill-gray-300'
                                                                }`}
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                                            </svg>
                                                        ))}
                                                    </div>
                                                    <span className="text-sm font-semibold text-alsero-dark-gray">
                                                        {categoryAvg > 0 ? categoryAvg.toFixed(1) : '0.0'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-red-600 transition-all duration-500"
                                                    style={{ width: `${categoryPercentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {allReviews.length > 0 && (
                        <div className="space-y-6 mb-8">
                            {allReviews.map((review) => {
                                const ratingCategories = review.rating_categories;

                                return (
                                    <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-6">
                                        <div className="flex items-start gap-4 mb-4">
                                            <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0 flex items-center justify-center">
                                                {review.image ? (
                                                    <img
                                                        src={transformMediaUrl(review.image, null) || review.image}
                                                        alt={review.name}
                                                        className="w-full h-full object-cover rounded-full"
                                                    />
                                                ) : (
                                                    <span className="text-gray-500 text-lg font-semibold">
                                                        {review.name.charAt(0).toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="font-semibold text-alsero-dark-gray">{review.name}</h4>
                                                    {review.submitted_at && (
                                                        <span className="text-sm text-alsero-light-gray">
                                                            {new Date(review.submitted_at).toLocaleDateString(
                                                                language === 'tr' ? 'tr-TR' : 'en-GB',
                                                                {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric',
                                                                },
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                {review.title && (
                                                    <h5 className="font-medium text-alsero-dark-gray mb-1">{review.title}</h5>
                                                )}
                                                <div className="flex text-yellow-400 mb-2">
                                                    {[...Array(5)].map((_, i) => (
                                                        <svg
                                                            key={i}
                                                            className={`w-4 h-4 ${
                                                                i < Math.round(review.rating) ? 'fill-current' : 'fill-gray-300'
                                                            }`}
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                                        </svg>
                                                    ))}
                                                </div>

                                                {ratingCategories && (
                                                    <div className="mb-3 space-y-2">
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                                            {PRODUCT_REVIEW_RATING_CATEGORIES.map((category) => {
                                                                const rating = resolveCategoryRating(
                                                                    ratingCategories,
                                                                    category.key,
                                                                    category.legacyKeys,
                                                                );
                                                                if (rating === undefined) return null;

                                                                return (
                                                                    <div key={category.key} className="flex items-center gap-2">
                                                                        <span className="text-alsero-light-gray min-w-[100px]">
                                                                            {reviewCategoryLabel(category, language, t)}:
                                                                        </span>
                                                                        <div className="flex text-yellow-400">
                                                                            {[...Array(5)].map((_, i) => (
                                                                                <svg
                                                                                    key={i}
                                                                                    className={`w-3 h-3 ${
                                                                                        i < Math.round(rating)
                                                                                            ? 'fill-current'
                                                                                            : 'fill-gray-300'
                                                                                    }`}
                                                                                    viewBox="0 0 20 20"
                                                                                >
                                                                                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                                                                </svg>
                                                                            ))}
                                                                        </div>
                                                                        <span className="text-xs text-alsero-dark-gray font-semibold">
                                                                            {rating.toFixed(1)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                <p className="text-alsero-text-tour">{review.comment}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {hasMoreReviews && onLoadMore && (
                        <div className="text-center mt-8">
                            <button
                                type="button"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                className="px-6 py-3 bg-alsero-first text-white rounded-lg font-semibold hover:bg-alsero-first/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loadingMore
                                    ? t('product_reviews.loading_more', 'Loading...')
                                    : t('product_reviews.load_more', 'Load More Reviews')}
                            </button>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}
